import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Search, X, ChevronUp, ChevronDown } from 'lucide-react'
import type { Node } from '@/service/graph'
import { parseName } from '@/service/graph'
import { useNodeSearch } from './useNodeSearch'
import { useTranslation } from '@/i18n'
import PreviewMatchList from '@/components/PreviewSearch/PreviewMatchList'
import type { PreviewMatch } from '@/components/PreviewSearch/types'

/** Stable empty set, so "no matches" doesn't churn the consumer's state either. */
const EMPTY_MATCH_IDS: Set<string> = new Set()

interface EditorSearchProps {
  nodes: Map<string, Node>
  isOpen: boolean
  onClose: () => void
  onMatchChange: (
    currentNodeId: string | null,
    allMatchIds: Set<string>
  ) => void
  /**
   * Where to put the match list. The bar floats over the drawing, but the list
   * must not — it would cover the very object you searched for. The parent
   * supplies a docked column (a flex sibling of the canvas) and the list is
   * portalled into it, so the canvas narrows instead of being painted over.
   * Without a host the list falls back to hanging under the bar.
   */
  listContainer?: HTMLElement | null
  /**
   * Open already searching for this, the way typing it would: the matches are
   * listed and the first is highlighted. Tag Mapping's preview seeds the tag.
   */
  initialSearch?: string
}

export default function EditorSearch({
  nodes,
  isOpen,
  onClose,
  onMatchChange,
  listContainer,
  initialSearch,
}: EditorSearchProps) {
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Seed the box the way typing would, so a seeded search and a typed one
  // cannot diverge (the PDF and image viewers' `initialSearch` do the same).
  useEffect(() => {
    const seed = initialSearch?.trim()
    if (seed) setSearchTerm(seed)
  }, [initialSearch])

  const { matchingNodeIds, currentMatchIndex, goToNext, goToPrevious, goTo } =
    useNodeSearch(nodes, debouncedSearchTerm)

  // FS-657: label every match so the list can show them all at once. Tags and
  // symbols interleave in node order, so the type goes in the row text rather
  // than a group header.
  const matches = useMemo<PreviewMatch[]>(
    () =>
      matchingNodeIds.map((id) => {
        const node = nodes.get(id)
        let snippet = id
        if (node?.type === 'tag') {
          snippet = [t('board:preview_search.tag_label'), node.content]
            .filter(Boolean)
            .join(' · ')
        } else if (node?.type === 'symbol') {
          const nameToUse =
            node.symbol_type && node.symbol_type !== 'null' ? node.symbol_type : node.name
          snippet = [
            t('board:preview_search.symbol_label'),
            parseName(nameToUse).label || nameToUse,
            node.label,
            node.pnid_level_iii,
          ]
            .filter(Boolean)
            .join(' · ')
        }
        return { id, locationLabel: '', snippet }
      }),
    [matchingNodeIds, nodes, t]
  )

  // One Set per result set, not per keystroke or per step. `useNodeSearch`
  // deliberately keeps `matchingNodeIds` referentially stable while the results
  // are unchanged; building a fresh Set in the dispatch below threw that away,
  // and the consumer stores it in state — so merely stepping to the next match
  // rebuilt every rectangle on the canvas (~2.4k on a dense sheet here).
  const matchIdSet = useMemo(() => new Set(matchingNodeIds), [matchingNodeIds])

  // Guards the onMatchChange dispatch: unrelated node-map updates
  // (e.g. user dragging nodes around) can't re-fire it and snap the
  // highlight back to the search match.
  const lastDispatchedSigRef = useRef<string>('')

  // Focus input when opened. Also reset the dispatch signature so reopening
  // the search with the same term re-fires onMatchChange (otherwise the
  // parent's highlight/zoom would never get set on reopen).
  useEffect(() => {
    if (isOpen) {
      if (inputRef.current) inputRef.current.focus()
      lastDispatchedSigRef.current = ''
    }
  }, [isOpen])

  // Debounce search term (300ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // One clamped index for BOTH consumers. A graph edit can shrink or reorder the
  // results while a later match is selected; passing the raw index to the list
  // while the canvas got a clamped one left the list with no active row (or the
  // two pointing at different nodes).
  const safeIndex =
    matchingNodeIds.length === 0 ? -1 : Math.min(currentMatchIndex, matchingNodeIds.length - 1)

  // Notify parent when matches or current index change.
  useEffect(() => {
    const currentId = safeIndex === -1 ? null : matchingNodeIds[safeIndex]
    const sig = `${currentId ?? ''}|${matchingNodeIds.join(',')}`
    if (sig === lastDispatchedSigRef.current) return
    lastDispatchedSigRef.current = sig

    if (matchingNodeIds.length === 0) {
      onMatchChange(null, EMPTY_MATCH_IDS)
      return
    }
    onMatchChange(currentId, matchIdSet)
  }, [matchingNodeIds, matchIdSet, safeIndex, onMatchChange])

  const handleClose = useCallback(() => {
    setSearchTerm('')
    setDebouncedSearchTerm('')
    // Reset so re-opening the search with the same term still dispatches.
    lastDispatchedSigRef.current = ''
    onClose()
  }, [onClose])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && matchingNodeIds.length > 0) {
      e.preventDefault()
      if (e.shiftKey) goToPrevious()
      else goToNext()
    }
    if (e.key === 'Escape') {
      handleClose()
    }
  }

  if (!isOpen) return null

  // FS-657: every matching object at once, instead of Enter-ing through them.
  const matchList = debouncedSearchTerm.trim() ? (
    <PreviewMatchList
      matches={matches}
      activeIndex={safeIndex}
      query={debouncedSearchTerm}
      onSelect={goTo}
      className={
        listContainer
          ? 'w-[264px] h-full border-l border-gray-200 dark:border-dark-border'
          : 'w-full border-t border-gray-200 dark:border-dark-border rounded-b-lg max-h-[280px]'
      }
    />
  ) : null

  return (
    <>
    <div className="absolute top-4 left-16 z-50 bg-white dark:bg-dark-card rounded-lg shadow-lg border border-gray-200 dark:border-dark-border flex flex-col min-w-[320px] max-w-[380px]">
      <div className="flex items-center gap-2 p-3">
      <Search className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
      <Input
        ref={inputRef}
        type="text"
        placeholder={t('graph_editor:search.nodesPlaceholder', { defaultValue: 'Search nodes...' })}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onKeyDown={handleKeyDown}
        className="flex-1 h-8 text-sm border-0 shadow-none focus-visible:ring-0"
      />

      {searchTerm && (
        <span className="text-xs text-gray-500 dark:text-dark-text-muted whitespace-nowrap">
          {matchingNodeIds.length > 0
            ? `${currentMatchIndex + 1}/${matchingNodeIds.length}`
            : t('graph_editor:search.noMatches', { defaultValue: 'No matches' })}
        </span>
      )}

      {matchingNodeIds.length > 1 && (
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={goToPrevious}
            className="h-8 w-8 p-0"
            title={t('graph_editor:search.previousMatch', { defaultValue: 'Previous match (Shift+Enter)' })}
          >
            <ChevronUp className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={goToNext}
            className="h-8 w-8 p-0"
            title={t('graph_editor:search.nextMatch', { defaultValue: 'Next match (Enter)' })}
          >
            <ChevronDown className="w-4 h-4" />
          </Button>
        </>
      )}

      <Button
        variant="ghost"
        size="sm"
        onClick={handleClose}
        className="h-8 w-8 p-0"
        title={t('graph_editor:search.close', { defaultValue: 'Close (Escape)' })}
      >
        <X className="w-4 h-4" />
      </Button>
      </div>
      {!listContainer && matchList}
    </div>
    {listContainer && matchList ? createPortal(matchList, listContainer) : null}
    </>
  )
}
